<script setup>
  /* global defineProps, defineEmits */
  import { computed } from 'vue'
  import { formatReplayLanguage } from './replayLabels'
  import { intersectCaptureRanges } from '../../composables/useReplayRangeAnalysis'
  const props = defineProps({
    inventory: { type: Object, required: true },
    modelValue: { type: Object, required: true },
    analysis: { type: Object, default: null },
    analyzing: Boolean,
    analysisError: { type: String, default: '' },
    disabled: Boolean,
  })
  const emit = defineEmits(['update:modelValue', 'reanalyze'])
  const update = (field, value) => emit('update:modelValue', { ...props.modelValue, [field]: value })
  const captures = computed(() => props.analysis?.captures || [])
  const selected = computed(() => captures.value.filter((capture) => capture.selected))
  const explicit = computed(() => props.modelValue.sourceSelectionMode === 'EXPLICIT')
  const local = (ms) => {
    if (!ms) return ''
    const date = new Date(ms)
    return new Date(ms - date.getTimezoneOffset() * 60000).toISOString().slice(0, 19)
  }
  const display = (ms) => (ms ? new Date(ms).toLocaleString() : '—')
  const duration = (ms) =>
    ms == null ? '待核实' : `${Math.floor(ms / 60000)} 分 ${Math.floor((ms % 60000) / 1000)} 秒`
  const toggle = (id, checked) =>
    update(
      'captureSessionIds',
      checked
        ? [...props.modelValue.captureSessionIds, id]
        : props.modelValue.captureSessionIds.filter((value) => value !== id)
    )
  const changeMode = (checked) =>
    emit('update:modelValue', {
      ...props.modelValue,
      sourceSelectionMode: checked ? 'EXPLICIT' : 'AUTO',
      captureSessionIds: checked ? selected.value.map((capture) => capture.captureSessionId) : [],
    })
  const setTime = (index, field, value) =>
    update(
      'ranges',
      props.modelValue.ranges.map((range, i) =>
        i === index ? { ...range, [field]: value ? new Date(value).getTime() : null } : range
      )
    )
  const addRange = () => update('ranges', [...props.modelValue.ranges, { startAtMs: null, endAtMs: null }])
  const removeRange = (index) =>
    update(
      'ranges',
      props.modelValue.ranges.filter((_, i) => i !== index)
    )
  const useCaptureRanges = () => update('ranges', intersectCaptureRanges(props.modelValue.ranges, selected.value))
</script>
<template>
  <fieldset class="range-editor" :disabled="disabled">
    <legend>选择正式内容</legend>
    <div class="range-heading">
      <strong>1. 保留时间段（本地时间）</strong>
      <el-button :disabled="disabled || analyzing || !selected.length" size="small" @click="useCaptureRanges">
        按采集分段，排除间隔
      </el-button>
    </div>
    <label>
      休会处理
      <select :value="modelValue.pausePolicy || 'PRESERVE'" @change="update('pausePolicy', $event.target.value)">
        <option value="SKIP_BREAKS">跳过长时间休会空档</option>
        <option value="PRESERVE">保留所选时间段完整时长</option>
      </select>
    </label>
    <div v-for="(range, index) in modelValue.ranges" :key="index" class="range-row">
      <input
        :aria-label="`第 ${index + 1} 段开始时间`"
        step="1"
        type="datetime-local"
        :value="local(range.startAtMs)"
        @change="setTime(index, 'startAtMs', $event.target.value)"
      />
      <span>至</span>
      <input
        :aria-label="`第 ${index + 1} 段结束时间`"
        step="1"
        type="datetime-local"
        :value="local(range.endAtMs)"
        @change="setTime(index, 'endAtMs', $event.target.value)"
      />
      <el-button :disabled="disabled || modelValue.ranges.length < 2" size="small" @click="removeRange(index)">
        移除
      </el-button>
    </div>
    <el-button :disabled="disabled || modelValue.ranges.length >= 100" size="small" @click="addRange">
      添加时间段
    </el-button>
    <div aria-live="polite" class="material-summary">
      <p v-if="analyzing">正在匹配范围内的素材…</p>
      <p v-else-if="analysisError" class="error" role="alert">
        {{ analysisError }}
        <el-button :disabled="disabled" size="small" @click="emit('reanalyze')">重试</el-button>
      </p>
      <template v-else-if="analysis">
        <strong>
          {{ explicit ? '已选' : '自动匹配' }} {{ selected.length }} 段采集 · 视频 {{ duration(analysis.durationMs) }}
        </strong>
        <p>
          原声{{ analysis.estimatedAudioMs == null ? '时长' : '约' }} {{ duration(analysis.estimatedAudioMs) }} ·
          原文字幕 {{ analysis.originalSubtitleCount }} 条 · PPT {{ analysis.pptEvents }} 次
        </p>
        <p v-if="analysis.skippedBreakMs > 0">已跳过休会 {{ duration(analysis.skippedBreakMs) }}</p>
        <p v-if="!selected.length" class="muted">当前范围没有选中的采集素材，将使用会议背景生成基础版。</p>
        <p v-if="analysis.sourceConflict" class="error" role="alert">
          存在重叠采集，请在高级来源选择中排除重复来源后预检。
        </p>
      </template>
      <p v-else class="muted">选择有效时间段后匹配素材。</p>
    </div>
    <details class="sources" :open="explicit || analysis?.sourceConflict">
      <summary>高级来源选择{{ explicit ? ' · 已限制来源' : '' }}</summary>
      <label class="capture-choice">
        <input
          :checked="explicit"
          :disabled="disabled || analyzing || !analysis"
          type="checkbox"
          @change="changeMode($event.target.checked)"
        />
        手动限制采集来源
      </label>
      <div v-for="capture in captures" :key="capture.captureSessionId" class="capture">
        <label class="capture-choice">
          <input
            v-if="explicit"
            :checked="capture.selected"
            :disabled="disabled || analyzing"
            type="checkbox"
            @change="toggle(capture.captureSessionId, $event.target.checked)"
          />
          <strong>{{ display(capture.startAtMs) }} — {{ display(capture.endAtMs) }}</strong>
          <span v-if="!explicit" class="muted">已纳入</span>
        </label>
        <p class="muted">范围内原声 {{ duration(capture.audioDurationMs) }} · 字幕 {{ capture.subtitles }} 条</p>
      </div>
    </details>
    <div class="options">
      <div>
        <strong>2. 字幕配音（可选）</strong>
        <p class="muted">原声默认保留；未选择配音语种也会生成原声版本。</p>
        <p v-if="analyzing" class="muted">正在更新可用语种…</p>
        <el-checkbox-group
          v-else-if="analysis?.languageOptions?.length"
          :disabled="disabled"
          :model-value="modelValue.languages"
          @update:model-value="update('languages', $event)"
        >
          <el-checkbox v-for="option in analysis.languageOptions" :key="option.language" :label="option.language">
            {{ formatReplayLanguage(option.language) }} · {{ option.subtitleCount }} 条 ·
            {{ duration(option.coverageMs) }}
          </el-checkbox>
        </el-checkbox-group>
        <p v-else class="muted">{{ analysis ? '所选范围没有可用的翻译字幕' : '选择时间段后查看可用语种' }}</p>
      </div>
      <strong>3. 画面与生成范围</strong>
      <label>
        <input
          :checked="modelValue.inheritPreviousPpt"
          type="checkbox"
          @change="update('inheritPreviousPpt', $event.target.checked)"
        />
        使用所选起点之前最后一页 PPT
      </label>
      <label>
        缺失画面背景
        <select
          :value="modelValue.bannerIndex == null ? '' : modelValue.bannerIndex"
          @change="update('bannerIndex', $event.target.value === '' ? null : Number($event.target.value))"
        >
          <option value="">默认会议背景</option>
          <option v-for="banner in inventory.banners" :key="banner.index" :value="banner.index">
            会议 banner {{ banner.index + 1 }} {{ formatReplayLanguage(banner.language, '') }}
          </option>
        </select>
      </label>
      <label>
        生成范围
        <select :value="modelValue.generationIntent" @change="update('generationIntent', $event.target.value)">
          <option value="PARTIAL">生成阶段回顾，会议仍可继续</option>
          <option value="FULL">确认会议已结束，生成整场回顾</option>
        </select>
      </label>
    </div>
  </fieldset>
</template>
<style scoped>
  .range-editor {
    min-width: 0;
    padding: 20px;
    border: 1px solid #dfe5ed;
    border-radius: 8px;
  }
  legend {
    padding: 0 8px;
    font-size: 18px;
    font-weight: 600;
  }
  .capture {
    padding: 14px 0;
    border-bottom: 1px solid #edf0f5;
  }
  .capture-choice {
    display: flex;
    align-items: baseline;
    gap: 10px;
  }
  p {
    margin: 8px 0;
    font-size: 13px;
    line-height: 1.6;
  }
  .muted {
    color: #707a8a;
  }
  .range-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    margin-top: 0;
    gap: 8px;
  }
  .range-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 12px 0;
    gap: 8px;
  }
  input[type='datetime-local'],
  select {
    max-width: 100%;
    padding: 8px;
    border: 1px solid #cbd3df;
    border-radius: 4px;
    background: white;
  }
  .options {
    display: grid;
    margin-top: 24px;
    gap: 18px;
  }
  .options label {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  .material-summary {
    margin-top: 18px;
    padding: 14px;
    background: #f3f7fc;
    border-radius: 6px;
  }
  .sources {
    margin-top: 14px;
  }
  .sources summary {
    cursor: pointer;
    padding: 10px 0;
  }
  .error {
    color: #b73838;
  }
  .capture p {
    overflow-wrap: anywhere;
  }
  .options :deep(.el-checkbox) {
    height: auto;
    margin-top: 8px;
  }
</style>
